import React, { useEffect, useRef, useState } from 'react';
import { trackAdEvent } from '../services/api';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AD_SIZES = {
  banner: { width: '100%', height: '90px', label: 'Banner Ad (728x90)' },
  leaderboard: { width: '100%', height: '90px', label: 'Leaderboard (728x90)' },
  rectangle: { width: '300px', height: '250px', label: 'Medium Rectangle' },
  skyscraper: { width: '160px', height: '600px', label: 'Wide Skyscraper' },
  largeRectangle: { width: '336px', height: '280px', label: 'Large Rectangle' },
  mobileBanner: { width: '100%', height: '50px', label: 'Mobile Banner' },
  inFeed: { width: '100%', height: '120px', label: 'In-Feed Ad' },
  betweenPanels: { width: '100%', height: '100px', label: 'Between Panels' },
  chapterEnd: { width: '100%', height: '150px', label: 'Chapter End' },
  sidebar: { width: '300px', height: '250px', label: 'Sidebar Ad' },
};

// Map size to ad_type for backend
const AD_TYPE_MAP = {
  banner: 'banner',
  leaderboard: 'banner',
  rectangle: 'sidebar',
  skyscraper: 'sidebar',
  largeRectangle: 'sidebar',
  mobileBanner: 'banner',
  inFeed: 'in_feed',
  betweenPanels: 'between_panels',
  chapterEnd: 'chapter_end',
  sidebar: 'sidebar',
};

// Global ad code cache
let adCodeCache = null;
let adCodeCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

async function getAdCodes() {
  const now = Date.now();
  if (adCodeCache && (now - adCodeCacheTime) < CACHE_DURATION) {
    return adCodeCache;
  }
  try {
    const res = await api.get('/ads/active');
    adCodeCache = res.data;
    adCodeCacheTime = now;
    return adCodeCache;
  } catch (error) {
    console.debug('Failed to fetch ad codes:', error);
    return [];
  }
}

export function AdBanner({ 
  size = 'banner', 
  className = '',
  showLabel = false,
  variant = 'default',
  placement = 'in_content',
  comicId,
  episodeId,
  creatorId,
  onImpression,
  onClick
}) {
  const { isAuthenticated } = useAuth();
  const adRef = useRef(null);
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
  const [adCode, setAdCode] = useState(null);
  const [adId, setAdId] = useState(null);
  
  const adConfig = AD_SIZES[size] || AD_SIZES.banner;
  const adType = AD_TYPE_MAP[size] || 'banner';
  
  // Fetch ad code on mount
  useEffect(() => {
    async function fetchAd() {
      const codes = await getAdCodes();
      // Find matching ad by type or placement
      const matchingAd = codes.find(ad => 
        ad.ad_type === adType || ad.placement === placement
      ) || codes[0]; // Fallback to first ad
      
      if (matchingAd) {
        setAdCode(matchingAd.ad_code);
        setAdId(matchingAd.id);
      }
    }
    fetchAd();
  }, [adType, placement]);
  
  // Track impression when ad becomes visible
  useEffect(() => {
    if (!adId || hasTrackedImpression) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasTrackedImpression) {
            trackImpression();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    }

    return () => observer.disconnect();
  }, [adId, hasTrackedImpression]);

  const trackImpression = async () => {
    if (!adId) return;
    
    try {
      await api.post(`/ads/${adId}/impression`);
      setHasTrackedImpression(true);
      if (onImpression) onImpression();
      
      // Also track for creator revenue if applicable
      if (comicId && isAuthenticated) {
        await trackAdEvent({
          ad_type: adType,
          event_type: 'impression',
          comic_id: comicId,
          episode_id: episodeId || null,
          ad_unit_id: adId
        });
      }
    } catch (error) {
      console.debug('Ad impression tracking failed:', error);
    }
  };

  const handleClick = async () => {
    if (!adId) return;
    
    try {
      await api.post(`/ads/${adId}/click`);
      if (onClick) onClick();
      
      if (comicId && isAuthenticated) {
        await trackAdEvent({
          ad_type: adType,
          event_type: 'click',
          comic_id: comicId,
          episode_id: episodeId || null,
          ad_unit_id: adId
        });
      }
    } catch (error) {
      console.debug('Ad click tracking failed:', error);
    }
  };
  
  const variantStyles = {
    default: 'bg-[var(--surface)]/50 border border-[var(--border)]/50',
    subtle: 'bg-[var(--surface)]/30 border border-[var(--border)]/30',
    prominent: 'bg-gradient-to-r from-[var(--surface)] via-[var(--surface)] to-[var(--surface)] border border-[var(--border)]',
  };

  // If we have a real ad code, render it
  if (adCode) {
    return (
      <div 
        ref={adRef}
        className={`${variantStyles[variant]} ${className}`}
        style={{ 
          width: adConfig.width, 
          minHeight: adConfig.height,
          maxWidth: '100%'
        }}
        onClick={handleClick}
        data-testid={`ad-${size}`}
        dangerouslySetInnerHTML={{ __html: adCode }}
      />
    );
  }

  // Fallback placeholder when no ad code is configured
  return (
    <div 
      ref={adRef}
      className={`flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${variantStyles[variant]} ${className}`}
      style={{ 
        width: adConfig.width, 
        minHeight: adConfig.height,
        maxWidth: '100%'
      }}
      onClick={handleClick}
      data-testid={`ad-${size}`}
    >
      <div className="text-center px-4">
        <span className="text-[var(--text-secondary)]/50 text-sm font-medium">
          {showLabel ? adConfig.label : 'Advertisement'}
        </span>
        <p className="text-xs text-[var(--text-secondary)]/30 mt-1">
          Ad space • Creators earn 40%
        </p>
      </div>
    </div>
  );
}

// Inline ad between content sections
export function InlineAd({ className = '', comicId, episodeId }) {
  return (
    <div className={`page-padding ${className}`}>
      <AdBanner 
        size="leaderboard" 
        variant="subtle" 
        comicId={comicId}
        episodeId={episodeId}
      />
    </div>
  );
}

// Ad between comic panels
export function BetweenPanelsAd({ comicId, episodeId }) {
  return (
    <div className="my-4 mx-auto max-w-2xl">
      <AdBanner 
        size="betweenPanels" 
        variant="subtle"
        comicId={comicId}
        episodeId={episodeId}
        showLabel={false}
      />
    </div>
  );
}

// Ad at end of chapter
export function ChapterEndAd({ comicId, episodeId }) {
  return (
    <div className="mt-8 mb-4 p-4 bg-[var(--surface)] rounded-lg">
      <p className="text-xs text-[var(--text-secondary)] mb-2 text-center">
        Support the creator by checking out our sponsors
      </p>
      <AdBanner 
        size="chapterEnd" 
        variant="prominent"
        comicId={comicId}
        episodeId={episodeId}
      />
    </div>
  );
}

// Sidebar ad
export function SidebarAd({ comicId }) {
  return (
    <div className="sticky top-20">
      <AdBanner 
        size="sidebar" 
        variant="default"
        comicId={comicId}
      />
    </div>
  );
}

export default AdBanner;
